// Reads the text file in a WhatsApp "Export chat". It runs on the device only. Nothing here talks
// to the server.
//
// Android writes one message per line as `31/12/2023, 21:41 - Name: text`. iOS writes
// `[31/12/2023, 21:41:05] Name: text`. The date order follows the phone's region, the clock can be
// 12 or 24 hours, and the file never says which, so both are worked out from the whole file.

export type DateOrder = "dmy" | "mdy" | "ymd";

/** What WhatsApp wrote in place of a photo, video or file the person left out of the export. */
export type OmittedKind = "media" | "image" | "video" | "audio" | "sticker" | "gif" | "document" | "contact";

export type WhatsAppMessage = {
  /** The line the message starts on, counting from 0. */
  line: number;
  /**
   * The time as the export shows it, such as "2023-12-31T21:41:05". The export has no time zone,
   * so this is the clock time on the phone that exported it.
   */
  time: string;
  /** The name or phone number WhatsApp showed. Null for lines WhatsApp wrote itself. */
  sender: string | null;
  body: string;
  /** The name of a file that came with the export, such as "IMG-20231231-WA0001.jpg". */
  attachment?: string;
  omitted?: OmittedKind;
  deleted?: boolean;
  edited?: boolean;
  /** A line such as "Ama added Ben" or the encryption notice. */
  system?: boolean;
};

export type ParsedChat = {
  format: "android" | "ios";
  dateOrder: DateOrder;
  clock: "12h" | "24h";
  messages: WhatsAppMessage[];
  /** Everyone who wrote a message, in the order they first appear. */
  senders: string[];
};

export class NotAWhatsAppExport extends Error {
  constructor() {
    super("This file is not a WhatsApp chat export.");
  }
}

// Invisible direction marks. iOS puts a left-to-right mark before attachments and its own lines.
const MARKS = /[‎‏‪-‮⁦-⁩﻿]/g;
const LEADING_MARKS = /^[‎‏‪-‮⁦-⁩﻿]+/;
const LRM = "‎";

const DATE = String.raw`(\d{1,4})([./-])(\d{1,2})\2(\d{1,4})`;
const TIME = String.raw`(\d{1,2})[:.](\d{2})(?:[:.](\d{2}))?(?:\s?([aApP])\.?\s?[mM]\.?)?`;
const IOS_HEADER = new RegExp(String.raw`^\[${DATE},?\s+${TIME}\]\s?`);
const ANDROID_HEADER = new RegExp(String.raw`^${DATE},?\s+${TIME}\s?[-–]\s`);

type Header = {
  format: "android" | "ios";
  a: number;
  b: number;
  c: number;
  aDigits: number;
  separator: string;
  hour: number;
  minute: number;
  second: number;
  meridiem: "a" | "p" | null;
  rest: string;
};

/** Spaces that WhatsApp uses around the clock, as plain spaces, so offsets stay the same. */
function plainSpaces(line: string): string {
  return line.replace(/[   ]/g, " ");
}

function readHeader(raw: string): Header | null {
  const line = raw.replace(LEADING_MARKS, "");
  const plain = plainSpaces(line);
  let format: Header["format"] = "ios";
  let m = IOS_HEADER.exec(plain);
  if (!m) {
    format = "android";
    m = ANDROID_HEADER.exec(plain);
  }
  if (!m) return null;
  const [, a, separator, b, c, hour, minute, second, meridiem] = m;
  return {
    format,
    a: Number(a),
    b: Number(b),
    c: Number(c),
    aDigits: a!.length,
    separator: separator!,
    hour: Number(hour),
    minute: Number(minute),
    second: second ? Number(second) : 0,
    meridiem: meridiem ? (meridiem.toLowerCase() as "a" | "p") : null,
    rest: line.slice(m[0].length),
  };
}

type Ymd = { year: number; month: number; day: number };

function toYmd(h: Header, order: DateOrder): Ymd | null {
  const [year, month, day] =
    order === "ymd" ? [h.a, h.b, h.c] : order === "dmy" ? [h.c, h.b, h.a] : [h.c, h.a, h.b];
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year: year < 100 ? 2000 + year : year, month, day };
}

function hour24(h: Header): number {
  if (!h.meridiem) return h.hour;
  const base = h.hour % 12;
  return h.meridiem === "p" ? base + 12 : base;
}

const pad = (n: number, width = 2) => String(n).padStart(width, "0");

function isoOf(d: Ymd, h: Header): string {
  return `${pad(d.year, 4)}-${pad(d.month)}-${pad(d.day)}T${pad(hour24(h))}:${pad(h.minute)}:${pad(h.second)}`;
}

/**
 * Works out whether 03/04 is the 3rd of April or March the 4th. A day above 12 settles it.
 * Otherwise the order that keeps the messages in time order wins, since exports are written oldest
 * first. A tie goes to month first for a 12-hour clock with slashes, as US phones write it.
 */
export function inferDateOrder(headers: Header[]): DateOrder {
  if (headers.some((h) => h.aDigits === 4)) return "ymd";
  const firstOver12 = headers.some((h) => h.a > 12);
  const secondOver12 = headers.some((h) => h.b > 12);
  if (firstOver12 && !secondOver12) return "dmy";
  if (secondOver12 && !firstOver12) return "mdy";
  const backwards = (order: DateOrder) => {
    let count = 0;
    let previous = "";
    for (const h of headers) {
      const d = toYmd(h, order);
      if (!d) {
        count += 1;
        continue;
      }
      const t = isoOf(d, h);
      if (t < previous) count += 1;
      previous = t;
    }
    return count;
  };
  const dmy = backwards("dmy");
  const mdy = backwards("mdy");
  if (dmy !== mdy) return dmy < mdy ? "dmy" : "mdy";
  const twelveHour = headers.some((h) => h.meridiem);
  return twelveHour && headers[0]?.separator === "/" ? "mdy" : "dmy";
}

// What WhatsApp writes, in English and a few common languages.
const EDITED =
  /\s*‎?<(?:This message was edited|Diese Nachricht wurde bearbeitet|Se editó este mensaje\.?|Ce message a été modifié|Mensagem editada|Questo messaggio è stato modificato|Dit bericht is bewerkt)>\s*$/;
const DELETED =
  /^(?:This message was deleted|You deleted this message|Diese Nachricht wurde gelöscht|Du hast diese Nachricht gelöscht|Se eliminó este mensaje|Eliminaste este mensaje|Ce message a été supprimé|Vous avez supprimé ce message|Essa mensagem foi apagada|Você apagou esta mensagem|Questo messaggio è stato eliminato|Dit bericht is verwijderd)\.?$/i;
const ANDROID_OMITTED =
  /^<[^<>\n]*(?:omitted|ausgeschlossen|omitido|omis|weggelaten|oculta|omessi|omesso)[^<>\n]*>$/i;
const IOS_OMITTED: [RegExp, OmittedKind][] = [
  [
    /(?:^|\s)(?:image|Bild|imagen|image absente|imagem|immagine|afbeelding) (?:omitted|weggelassen|omitida|omise|ocultada|omessa|weggelaten)$/i,
    "image",
  ],
  [
    /(?:^|\s)(?:video|Video|vidéo|vídeo) (?:omitted|weggelassen|omitido|omise|ocultado|omesso|weggelaten)$/i,
    "video",
  ],
  [/(?:^|\s)(?:audio|Audio) (?:omitted|weggelassen|omitido|omis|ocultado|omesso|weggelaten)$/i, "audio"],
  [
    /(?:^|\s)(?:sticker|Sticker) (?:omitted|weggelassen|omitido|omis|ocultado|omesso|weggelaten)$/i,
    "sticker",
  ],
  [/(?:^|\s)GIF (?:omitted|weggelassen|omitido|omis|ocultado|omesso|weggelaten)$/i, "gif"],
  [
    /(?:^|\s)(?:document|Dokument|documento) (?:omitted|weggelassen|omitido|omis|ocultado|omesso|weggelaten)$/i,
    "document",
  ],
  [
    /(?:^|\s)(?:Contact card|Kontaktkarte|tarjeta de contacto|fiche contact) (?:omitted|weggelassen|omitida|omise|omessa)$/i,
    "contact",
  ],
];
const IOS_ATTACHED = /^<[^:<>\n]{2,30}:\s?([^<>\n]+?\.[A-Za-z0-9]{1,8})>\s*([\s\S]*)$/;
const ANDROID_ATTACHED =
  /^([^\n]+?\.[A-Za-z0-9]{1,8}) \((?:file attached|Datei angehängt|archivo adjunto|fichier joint|arquivo anexado|file allegato|bestand bijgevoegd)\)(?:\n([\s\S]*))?$/i;
// Android lines without a colon are WhatsApp's own. These catch the few that have one.
const ANDROID_SYSTEM_NAME =
  /["“”]|\s(?:changed|created|added|removed|left|joined|deleted|turned|pinned|started|blocked|unblocked)\s/;

function classify(raw: {
  line: number;
  time: string;
  format: Header["format"];
  rest: string;
}): WhatsAppMessage {
  const base = { line: raw.line, time: raw.time };
  const colon = raw.rest.indexOf(": ");
  if (colon <= 0) return { ...base, sender: null, body: raw.rest.replace(MARKS, "").trim(), system: true };
  const sender = raw.rest.slice(0, colon).replace(MARKS, "").trim();
  let body = raw.rest.slice(colon + 2);
  if (raw.format === "android" && ANDROID_SYSTEM_NAME.test(sender)) {
    return { ...base, sender: null, body: raw.rest.replace(MARKS, "").trim(), system: true };
  }
  const marked = body.startsWith(LRM);
  const message: WhatsAppMessage = { ...base, sender, body: "" };

  if (EDITED.test(body)) {
    message.edited = true;
    body = body.replace(EDITED, "");
  }
  const clean = body.replace(LEADING_MARKS, "");
  const firstLine = clean.split("\n", 1)[0]!.replace(MARKS, "").trim();

  const iosFile = IOS_ATTACHED.exec(clean.replace(MARKS, ""));
  if (iosFile) {
    message.attachment = iosFile[1]!.trim();
    message.body = iosFile[2]!.trim();
    return message;
  }
  const androidFile = ANDROID_ATTACHED.exec(clean.replace(MARKS, ""));
  if (androidFile) {
    message.attachment = androidFile[1]!.trim();
    message.body = (androidFile[2] ?? "").trim();
    return message;
  }
  if (ANDROID_OMITTED.test(firstLine) || firstLine === "null") {
    message.omitted = "media";
    message.body = clean.split("\n").slice(1).join("\n").trim();
    return message;
  }
  for (const [pattern, kind] of IOS_OMITTED) {
    if (pattern.test(firstLine)) {
      message.omitted = kind;
      // iOS writes a document's name before the note, as "Report.pdf • 3 pages document omitted".
      const name = firstLine.replace(pattern, "").split("•")[0]!.trim();
      message.body = name;
      return message;
    }
  }
  if (DELETED.test(firstLine)) {
    message.deleted = true;
    return message;
  }
  // iOS writes its own lines, such as "Ama added Ben", with a mark in front and the chat's name as
  // the sender. A shared location also starts with a mark and is a real message.
  if (
    raw.format === "ios" &&
    marked &&
    !/^(?:Location|Live location|Standort|Ubicación|Localisation|Localização|Posizione)\b/i.test(firstLine)
  ) {
    return { ...base, sender: null, body: clean.replace(MARKS, "").trim(), system: true };
  }
  message.body = clean.replace(/\s+$/, "");
  return message;
}

/** Parses the text of an exported chat. Throws NotAWhatsAppExport when no line looks like one. */
export function parseWhatsApp(text: string): ParsedChat {
  const lines = text.replace(/\r\n?/g, "\n").split("\n");
  const headers: (Header & { line: number })[] = [];
  const bodies: string[][] = [];
  for (const [i, line] of lines.entries()) {
    const h = readHeader(line);
    if (h) {
      headers.push({ ...h, line: i });
      bodies.push([h.rest]);
    } else if (bodies.length) {
      bodies[bodies.length - 1]!.push(line);
    }
  }
  if (!headers.length) throw new NotAWhatsAppExport();
  const iosCount = headers.filter((h) => h.format === "ios").length;
  const format = iosCount * 2 >= headers.length ? "ios" : "android";
  const dateOrder = inferDateOrder(headers);

  const messages: WhatsAppMessage[] = [];
  for (const [i, h] of headers.entries()) {
    const d = toYmd(h, dateOrder);
    if (!d) continue;
    const rest = bodies[i]!.join("\n").replace(/\n+$/, "");
    messages.push(classify({ line: h.line, time: isoOf(d, h), format: h.format, rest }));
  }

  const senders: string[] = [];
  for (const m of messages) if (m.sender && !m.system && !senders.includes(m.sender)) senders.push(m.sender);
  return {
    format,
    dateOrder,
    clock: headers.some((h) => h.meridiem) ? "12h" : "24h",
    messages,
    senders,
  };
}

/** Whether text starts like an exported chat. Reads only the first few lines. */
export function looksLikeWhatsAppText(text: string): boolean {
  const lines = text.slice(0, 4000).replace(/\r\n?/g, "\n").split("\n").slice(0, 8);
  return lines.some((line) => readHeader(line) !== null);
}

const TITLE_PREFIXES =
  /^(?:WhatsApp Chat with|WhatsApp Chat -|WhatsApp-Chat mit|Chat de WhatsApp con|Discussion WhatsApp avec|Conversa do WhatsApp com|Chat WhatsApp con|WhatsApp-chat met)\s+/i;

/**
 * The chat's name from the export's file name: "WhatsApp Chat with Ama.zip" from Android,
 * "WhatsApp Chat - Family.zip" from iOS. Anything else gives the file name without its extension.
 */
export function titleFromFileName(name: string): string {
  const base = (name.split(/[\\/]/).pop() ?? name)
    .replace(/\.(?:zip|txt)$/i, "")
    .replace(/\s*\(\d+\)$/, "")
    .trim();
  const stripped = base.replace(TITLE_PREFIXES, "").trim();
  if (stripped !== base) return stripped;
  if (!base || base === "_chat") return "WhatsApp chat";
  return base;
}

/** File names WhatsApp gives the chat text inside an export. */
export function isChatTextName(name: string): boolean {
  const base = name.split("/").pop() ?? name;
  return base === "_chat.txt" || (/\.txt$/i.test(base) && TITLE_PREFIXES.test(base.replace(/\.txt$/i, "")));
}
